import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineOppositeContent,
    timelineOppositeContentClasses
} from '@mui/lab';
import { Paper, Typography, Box, CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs'; // npm install dayjs
import useApiManager from '../../../utils/Api/apiManager';
import groupBy from 'lodash.groupby';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Link } from 'react-router-dom';

export default function EntityTimeline({ entityId, entityType }) {
    const { get } = useApiManager();
    const [changeLogs, setChangeLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchChangeLogs = async () => {
        try {
            setLoading(true);
            const data = await get(`/${entityType}/${entityId}/changelogs`);
            setChangeLogs(data);
        } catch (error) {
            console.error('Error fetching change logs:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchChangeLogs();
    }, [entityId, entityType]);

    dayjs.extend(utc);
    dayjs.extend(timezone);

    const groupedLogs = groupBy(changeLogs, log =>
        dayjs.utc(log.timestamp).local().format('MMMM D, YYYY')
    );

    return (
        <Paper elevation={0} sx={{ p: 2, display: 'flex', justifyContent: 'flex-start', minHeight: 350 }}>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Timeline
                    sx={{
                        [`& .${timelineOppositeContentClasses.root}`]: {
                            flex: 0.15,
                        },
                    }}
                >
                    {Object.entries(groupedLogs).map(([date, logs]) => (
                        <Box key={date}>
                            <Typography
                                variant="caption"
                                sx={{
                                    px: 1.5,
                                    py: 0.5,
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: 1,
                                    width: 'fit-content',
                                    mb: 1,
                                    fontWeight: 500,
                                }}
                            >
                                {dayjs(date).format('MMM D, YYYY')}
                            </Typography>

                            {logs.map((log, index) => (
                                <TimelineItem key={log.timestamp + index}>
                                    <TimelineOppositeContent color="text.secondary">
                                        {dayjs.utc(log.timestamp).local().format('hh:mm A')}
                                    </TimelineOppositeContent>
                                    <TimelineSeparator>
                                        <TimelineDot />
                                        {index !== logs.length - 1 && <TimelineConnector />}
                                    </TimelineSeparator>
                                    <TimelineContent>
                                        <Typography variant="body2" fontWeight={500}>
                                            {log.action} - <Link to={`${log.referenceLink}`}>View</Link>
                                        </Typography>
                                        <Typography variant="body2" fontWeight={100}>
                                            {log.changes}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            by {log.performedBy} {dayjs.utc(log.timestamp).local().format('MMM D, YYYY')}
                                        </Typography>
                                    </TimelineContent>
                                </TimelineItem>
                            ))}
                        </Box>
                    ))}
                </Timeline>
            )}
        </Paper>
    );
}
